import { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DollarSign, Plus, Pencil, Trash2, TrendingUp, Calendar, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TransportRevenue() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({ date: new Date().toISOString().slice(0, 10), reference: '', description: '', amount_ex_vat: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const formatCurrency = (amount) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[parseInt(mo)] || mo} ${y}`;
  };
  const formatMonthFull = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${names[parseInt(mo)] || mo} ${y}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (selectedMonth && selectedMonth !== 'all') params.month = selectedMonth;
      else if (selectedYear && selectedYear !== 'all') params.year = selectedYear;

      const [entryRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/transport-revenue/entries`, { headers, params }),
        axios.get(`${API_URL}/api/transport-revenue/summary`, { headers })
      ]);
      setEntries(entryRes.data);
      setSummary(summaryRes.data);
    } catch { toast.error('Failed to load revenue data'); }
    finally { setLoading(false); }
  }, [selectedMonth, selectedYear]);

  useEffect(() => { fetchData(); }, [selectedMonth, selectedYear]);

  // Default to current month
  useEffect(() => {
    if (summary && !selectedMonth && !selectedYear) {
      setSelectedMonth(summary.current_month);
    }
  }, [summary]);

  const handleAddEntry = async () => {
    if (!newEntry.amount_ex_vat) { toast.error('Amount is required'); return; }
    try {
      await axios.post(`${API_URL}/api/transport-revenue/entries`, newEntry, { headers });
      toast.success('Revenue entry added!');
      setNewEntry({ date: new Date().toISOString().slice(0, 10), reference: '', description: '', amount_ex_vat: '' });
      setShowAddEntry(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditEntry = async () => {
    if (!editingEntry) return;
    try {
      await axios.put(`${API_URL}/api/transport-revenue/entries/${editingEntry.id}`, {
        date: editingEntry.date, reference: editingEntry.reference, description: editingEntry.description,
        amount_ex_vat: parseFloat(editingEntry.amount_ex_vat) || 0
      }, { headers });
      toast.success('Updated!'); setShowEditEntry(false); setEditingEntry(null); fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this revenue entry?')) return;
    try { await axios.delete(`${API_URL}/api/transport-revenue/entries/${id}`, { headers }); toast.success('Deleted!'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const filteredTotal = entries.reduce((s, e) => s + (e.amount_ex_vat || 0), 0);

  // For the yearly chart
  const monthlyData = useMemo(() => {
    if (!summary?.monthly_breakdown) return [];
    return Object.entries(summary.monthly_breakdown).map(([month, total]) => ({ month, total }));
  }, [summary]);

  const maxMonthly = Math.max(...monthlyData.map(d => d.total), 1);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-slate-600">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-emerald-700" /> JSM Transport Revenue
            </h1>
            <p className="text-slate-600 text-sm">Revenue Generated from Services (Ex VAT)</p>
          </div>
          <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-revenue-btn"><Plus className="h-4 w-4 mr-1" /> Add Revenue</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-700" /> ADD REVENUE ENTRY</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Date *</Label><Input type="date" value={newEntry.date} onChange={e => setNewEntry({...newEntry, date: e.target.value})} className="bg-white border-slate-200" data-testid="revenue-date-input" /></div>
                <div><Label>Reference</Label><Input value={newEntry.reference} onChange={e => setNewEntry({...newEntry, reference: e.target.value})} placeholder="e.g., INV-001, DPD Contract" className="bg-white border-slate-200" data-testid="revenue-ref-input" /></div>
                <div><Label>Description</Label><Input value={newEntry.description} onChange={e => setNewEntry({...newEntry, description: e.target.value})} placeholder="e.g., Courier deliveries" className="bg-white border-slate-200" data-testid="revenue-desc-input" /></div>
                <div><Label>Amount Ex VAT (R) *</Label><Input type="number" step="0.01" value={newEntry.amount_ex_vat} onChange={e => setNewEntry({...newEntry, amount_ex_vat: e.target.value})} placeholder="0.00" className="bg-white border-slate-200" data-testid="revenue-amount-input" /></div>
                <Button onClick={handleAddEntry} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-revenue-btn">SAVE REVENUE ENTRY</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><Calendar className="h-4 w-4" /><span className="text-xs opacity-80">Month to Date</span></div>
              <p className="text-xl font-bold" data-testid="mtd-total">{formatCurrency(summary?.mtd_total)}</p>
              <p className="text-xs opacity-70">{summary?.mtd_count || 0} entries</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs opacity-80">Year to Date</span></div>
              <p className="text-xl font-bold" data-testid="ytd-total">{formatCurrency(summary?.ytd_total)}</p>
              <p className="text-xs opacity-70">{summary?.ytd_count || 0} entries</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-amber-500 to-amber-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><BarChart3 className="h-4 w-4" /><span className="text-xs opacity-80">Current View</span></div>
              <p className="text-xl font-bold">{formatCurrency(filteredTotal)}</p>
              <p className="text-xs opacity-70">{entries.length} entries</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-violet-500 to-violet-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><Calendar className="h-4 w-4" /><span className="text-xs opacity-80">Period</span></div>
              <p className="text-lg font-bold">{selectedMonth && selectedMonth !== 'all' ? formatMonthFull(selectedMonth) : selectedYear && selectedYear !== 'all' ? selectedYear : 'All Time'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="entries">
          <TabsList className="bg-slate-100 mb-4">
            <TabsTrigger value="entries" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-slate-700" data-testid="revenue-entries-tab">Revenue Entries</TabsTrigger>
            <TabsTrigger value="monthly" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white text-slate-700" data-testid="revenue-monthly-tab">Monthly Overview</TabsTrigger>
          </TabsList>

          {/* Revenue Entries Tab */}
          <TabsContent value="entries">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="min-w-[180px]">
                <Label className="text-slate-600 text-xs">Month</Label>
                <Select value={selectedMonth} onValueChange={v => { setSelectedMonth(v); setSelectedYear(''); }}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="revenue-month-filter"><SelectValue placeholder="Select month" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All Months</SelectItem>
                    {(summary?.available_months || []).map(m => <SelectItem key={m} value={m}>{formatMonthFull(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[120px]">
                <Label className="text-slate-600 text-xs">Year</Label>
                <Select value={selectedYear} onValueChange={v => { setSelectedYear(v); setSelectedMonth(''); }}>
                  <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="revenue-year-filter"><SelectValue placeholder="Year" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All Years</SelectItem>
                    {(summary?.available_years || []).map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-0">
                {entries.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">No revenue entries for this period. Click "Add Revenue" to start tracking.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-200">
                          <TableHead className="text-slate-600">Date</TableHead>
                          <TableHead className="text-slate-600">Reference</TableHead>
                          <TableHead className="text-slate-600">Description</TableHead>
                          <TableHead className="text-slate-600 text-right">Amount Ex VAT</TableHead>
                          <TableHead className="text-slate-600 text-right w-20">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map(e => (
                          <TableRow key={e.id} className="border-slate-200">
                            <TableCell className="text-slate-700 text-sm">{new Date(e.date).toLocaleDateString('en-ZA')}</TableCell>
                            <TableCell className="text-amber-700 font-medium text-sm">{e.reference || '-'}</TableCell>
                            <TableCell className="text-slate-900 text-sm">{e.description || '-'}</TableCell>
                            <TableCell className="text-emerald-700 text-right font-bold">{formatCurrency(e.amount_ex_vat)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-6 w-6 p-0" data-testid={`edit-rev-${e.id}`} onClick={() => { setEditingEntry({...e}); setShowEditEntry(true); }}><Pencil className="h-3 w-3" /></Button>
                                <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-6 w-6 p-0" data-testid={`del-rev-${e.id}`} onClick={() => handleDeleteEntry(e.id)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-slate-200 bg-slate-100">
                          <TableCell colSpan={3} className="text-slate-900 font-bold text-sm">TOTAL ({entries.length} entries)</TableCell>
                          <TableCell className="text-emerald-700 text-right font-bold text-lg">{formatCurrency(filteredTotal)}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Overview Tab */}
          <TabsContent value="monthly">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-slate-900 text-base">Monthly Revenue — {summary?.current_year}</CardTitle></CardHeader>
              <CardContent>
                {monthlyData.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">No data for this year yet.</p>
                ) : (
                  <div className="space-y-3">
                    {monthlyData.map(d => {
                      const pct = maxMonthly > 0 ? (d.total / maxMonthly) * 100 : 0;
                      return (
                        <div key={d.month} className="flex items-center gap-3 cursor-pointer" onClick={() => { setSelectedMonth(d.month); setSelectedYear(''); }}>
                          <div className="w-20 shrink-0 text-right">
                            <p className="text-slate-900 font-medium text-sm">{formatMonth(d.month)}</p>
                          </div>
                          <div className="flex-1 bg-slate-100 rounded-full h-8 overflow-hidden relative">
                            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500" style={{ width: `${Math.max(pct, 3)}%` }} />
                            <span className="absolute inset-0 flex items-center px-3 text-sm font-bold text-slate-900">{formatCurrency(d.total)}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-4 pt-3 border-t border-slate-200 flex justify-between items-center">
                      <span className="text-slate-900 font-bold">Year to Date Total</span>
                      <span className="text-emerald-700 font-bold text-xl">{formatCurrency(summary?.ytd_total)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={showEditEntry} onOpenChange={setShowEditEntry}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> EDIT REVENUE ENTRY</DialogTitle></DialogHeader>
            {editingEntry && (
              <div className="space-y-4">
                <div><Label>Date</Label><Input type="date" value={editingEntry.date} onChange={e => setEditingEntry({...editingEntry, date: e.target.value})} className="bg-white border-slate-200" /></div>
                <div><Label>Reference</Label><Input value={editingEntry.reference || ''} onChange={e => setEditingEntry({...editingEntry, reference: e.target.value})} className="bg-white border-slate-200" /></div>
                <div><Label>Description</Label><Input value={editingEntry.description || ''} onChange={e => setEditingEntry({...editingEntry, description: e.target.value})} className="bg-white border-slate-200" /></div>
                <div><Label>Amount Ex VAT (R)</Label><Input type="number" step="0.01" value={editingEntry.amount_ex_vat} onChange={e => setEditingEntry({...editingEntry, amount_ex_vat: e.target.value})} className="bg-white border-slate-200" /></div>
                <Button onClick={handleEditEntry} className="w-full bg-blue-600 hover:bg-blue-700">UPDATE</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
